import { storeWrapper } from '../storeWrapper'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useClientStore } from '@repo/store/useClientStore'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useCOISStore } from '@repo/store/useCOISStore'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { throttle } from '@repo/utils/debounce'
import { convertDateToLocalTimeInIsoFormat } from '@repo/utils/time'

const initialState = {
  isNew: true,
  newMatterNo: '',
  existingMatter: null,
  debtorLimit: '',
  wipLimit: '',
  disbursementsLimit: '',
  reviewDate: null,
  reviewPeriod: null,
  reviewType: null,
  prospect: false,
  defaultProspectValue: false,
  dateOpened: null,
  description: '',
  worktype: null,
  branch: null,
  department: null,
  feeEarner: null,
  supervisingFeeEarner: null,
  privateOrLegalAid: 'P',
  remunerationType: null,
  franchiseCategory: null,
  uniqueFileNoDate: null,
  uniqueFileNo: '',
  crmSchemeId: 0,
  crmScheme: null,
  restrictedAccess: false,
  groupPermissions: [],
  userPermissions: [],
  customDataPageFieldValues: [],
  extensionFields: [],
  extensionValues: {}
}

export const useNewMatterStore = storeWrapper('useNewMatterStore', (set, get) => ({
  ...initialState,
  updateState: ({ key, value }) => {
    set({ [key]: value })
  },
  updateWizardMatterState: ({
    matterData,
    branch,
    department,
    feeEarner,
    remunerationType,
    supervisingFeeEarner,
    workflowType,
    customDataPageFieldValues
  }) => {
    set({
      isNew: true,
      branch: branch ?? null,
      crmScheme: matterData.crmScheme ?? null,
      crmSchemeId: matterData.crmSchemeId ?? 0,
      dateOpened: matterData.dateOpened ?? null,
      debtorLimit: matterData.debtorLimit ?? '',
      department: department ?? null,
      description: matterData.description ?? '',
      disbursementsLimit: matterData.disbursementsLimit ?? '',
      existingMatter: matterData ?? null,
      feeEarner: feeEarner ?? null,
      franchiseCategory: matterData.franchiseCategory ?? null,
      groupPermissions: matterData.groupPermissions ?? [],
      privateOrLegalAid: matterData.privateOrLegalAid ?? 'P',
      prospect: matterData.prospect ?? false,
      remunerationType: remunerationType ?? null,
      restrictedAccess: matterData.restrictedAccess ?? false,
      reviewDate: matterData.reviewDate ?? null,
      reviewPeriod: matterData.reviewPeriod ?? null,
      reviewType: matterData.reviewType ?? null,
      supervisingFeeEarner: supervisingFeeEarner ?? null,
      uniqueFileNo: matterData.uniqueFileNo ?? '',
      uniqueFileNoDate: matterData.uniqueFileNo ?? null,
      userPermissions: matterData.userIdPermissions ?? [],
      wipLimit: matterData.wipLimit ?? '',
      worktype: workflowType ?? null,
      customDataPageFieldValues: customDataPageFieldValues ?? []
    })
  },
  getNewMatterPayload: () => {
    // if a new client is being created, use newClientNo. If an existing client was selected in the COIS wizard or in the matter wizard, use existingClient
    const {
      isNew,
      dateOpened,
      description,
      prospect,
      privateOrLegalAid,
      crmScheme,
      crmSchemeId,
      uniqueFileNo,
      wipLimit,
      disbursementsLimit,
      debtorLimit,
      reviewDate,
      reviewPeriod,
      restrictedAccess,
      groupPermissions,
      userPermissions,
      customDataPageFieldValues
    } = get()

    const { isNew: clientIsNew, newClientNo, existingClient } = useNewClientStore.getState()

    if (!isNew) {
      const clientNo = existingClient?.clientNo || ''

      return {
        isNew: false,
        clientNo: String(clientNo),
        matterNo: get().existingMatter?.matterNo || null,
        groupPermissions: [],
        userPermissions: [],
        customDataPageFieldValues: []
      }
    }

    const getCrmSchemeId = () => {
      if (crmScheme?.schemeId || crmScheme?.schemeId === 0) {
        return crmScheme.schemeId
      }

      if (crmSchemeId || crmSchemeId === 0) {
        return crmSchemeId
      }

      if (privateOrLegalAid === 'P') {
        return 0
      }

      return null
    }

    const dateToString = (date) => {
      if (!date) return null
      if (typeof date === 'string') return date
      return convertDateToLocalTimeInIsoFormat(date)
    }

    const clientNo = clientIsNew ? newClientNo : existingClient?.clientNo || ''

    const franchiseId = privateOrLegalAid === 'L' ? get().franchiseCategory?.franchiseId : null
    const franchiseTypeId = privateOrLegalAid === 'L' ? get().franchiseCategory?.franchiseTypeId : null

    return {
      isNew: true,
      clientNo: String(clientNo),
      matterNo: get().newMatterNo,
      dateOpened: dateToString(dateOpened),
      description,
      prospect,
      workflowTypeId: get().worktype?.workflowTypeId,
      branchId: get().branch?.branchId,
      departmentId: get().department?.departmentId,
      feeEarnerId: get().feeEarner?.feeEarnerId,
      supervisingFeeEarnerId: get().supervisingFeeEarner?.feeEarnerId,
      privateOrLegalAid,
      remunerationTypeId: get().remunerationType?.remunerationTypeId,
      crmSchemeId: getCrmSchemeId(),
      franchiseTypeId: franchiseId, // this looks wrong, and needs to be fixed. It is correct that we are sending the value of franchiseId, but the API param is named incorrectly. There is a ticket to fix this, and when that is done we'll need to change the param name here to franchiseId
      uniqueFileNo: franchiseTypeId && franchiseTypeId !== 'OTH' && uniqueFileNo ? uniqueFileNo : null,
      debtorLimit: debtorLimit || null,
      disbursementsLimit: disbursementsLimit || null,
      wipLimit: wipLimit || null,
      reviewDate: dateToString(reviewDate),
      reviewPeriod: reviewPeriod || 0,
      reviewTypeId: get().reviewType?.reviewTypeId || null,
      restrictedAccess,
      securityGroupIdPermissions: groupPermissions?.length
        ? groupPermissions.map((group) => group.securityGroupId)
        : [],
      userIdPermissions: userPermissions?.length ? userPermissions.map((user) => user.userId) : [],
      customDataPageFieldValues
    }
  },
  saveNewMatterWizard: async ({ wizardId }) => {
    return new Promise((resolve, reject) => {
      throttle({
        id: `saveNewMatterWizard`,
        callback: async () => {
          const { getNewMatterPayload } = get()
          const { getConflictOfInterestPartyRequestAndResponses, getConflictOfInterestAcknowledgementPayload } =
            useCOISStore.getState()
          const { getNewClientPayload } = useNewClientStore.getState()

          const matter = getNewMatterPayload()
          const client = getNewClientPayload()
          const payload = { client, matter }

          let endpoint = 'Matter/CreateMatter'

          if (wizardId.includes('COIS')) {
            payload.conflictOfInterestPartyRequestAndResponses = getConflictOfInterestPartyRequestAndResponses()
            payload.conflictOfInterestAcknowledgement = getConflictOfInterestAcknowledgementPayload()
            endpoint = 'Matter/CreateClientMatterAndConflictOfInterestSearch'
          } else if (wizardId.includes('NewClient')) {
            endpoint = 'Matter/CreateClientAndMatter'
          }

          // check client no is still unique
          if (payload?.client?.isNew && payload?.client?.clientNo) {
            const { checkIfClientNoExists } = useClientStore.getState()
            const exists = await checkIfClientNoExists({
              clientNo: payload.client.clientNo
            })

            if (exists) {
              reject('Client number already exists')
              return
            }
          }

          // a "client number already exists" retry replaces the client no after the matter no was
          // earmarked against the previous client — re-derive the matter no for the client actually
          // being created (typically 1 for a new client, but always derived, never assumed)
          if (payload?.client?.isNew && payload?.matter?.isNew && payload?.client?.clientNo) {
            const { fetchNextMatterNo } = useMattersStore.getState()
            const nextMatterNo = await fetchNextMatterNo({ clientNo: payload.client.clientNo })

            if (nextMatterNo && String(nextMatterNo) !== String(payload.matter.matterNo)) {
              payload.matter.matterNo = nextMatterNo
              get().updateState({ key: 'newMatterNo', value: nextMatterNo })
            }
          }

          //check matter no is still unique
          if (payload?.matter?.isNew && payload?.client?.clientNo && payload?.matter?.matterNo) {
            const { checkIfMatterNoExists } = useMattersStore.getState()
            const exists = await checkIfMatterNoExists({
              clientNo: payload.client.clientNo,
              matterNo: payload.matter.matterNo
            })

            if (exists) {
              reject('Matter number already exists')
              return
            }
          }

          // check ufn is still unique
          if (payload?.matter?.isNew && payload?.matter?.uniqueFileNo) {
            const { checkIfUFNExists } = useMattersStore.getState()
            const exists = await checkIfUFNExists({
              ufn: payload.matter.uniqueFileNo,
              clientNo: payload.client.clientNo
            })

            if (exists) {
              reject('Unique file number already exists')
              return
            }
          }

          try {
            const res = ospreyAPI({
              service: 'matters',
              endpoint,
              method: 'POST',
              data: payload
            })

            resolve(res)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  resetWizardMatterState: () => {
    const { defaultProspectValue } = get()
    set({ ...initialState, defaultProspectValue, prospect: defaultProspectValue })
  },
  reset: () => {
    // the default value for prospect was retrieved from the server, so we don't want to reset it
    const { defaultProspectValue } = get()
    set({ ...initialState, defaultProspectValue, prospect: defaultProspectValue })
  }
}))
